<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <validation-observer ref="news">
            <b-row>
                <b-col md="8" lg="8">
                    <b-card no-body>
                        <div class="card-header pb-1">
                            <div class="card-title">General</div>
                            <div class="text-right">
                                <b-form-checkbox
                                    checked="true"
                                    name="check-button"
                                    class="mb-1"
                                    switch
                                    inline
                                    v-model="news.visible"
                                >
                                    Is news visible?
                                </b-form-checkbox>
                            </div>
                        </div>

                        <b-tabs class="ml-2 mr-2 mb-1">
                            <b-tab
                                v-for="language in languages"
                                :title="language.code.toUpperCase()"
                            >
                                <b-form-group
                                    :label="$t('Component(Label):name')"
                                    :label-for="'name' + language.code"
                                >
                                    <validation-provider
                                        #default="{ errors }"
                                        :name="language.title + ' ' + $t('Component(Label):name')"
                                        :rules="language.code === 'en' ? 'required|max:255|min:3' : null"
                                    >
                                        <b-form-input
                                            :state="errors.length > 0 ? false:null"
                                            :id="'name' + language.code"
                                            v-model="news.translations[language.code].title"
                                            :placeholder="$t('Component(Placeholder):name')"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>

                                <b-form-group
                                    :label="$t('Component(Label):short-description')"
                                    :label-for="'short-description' + language.code"
                                >
                                    <validation-provider
                                        #default="{ errors }"
                                        :name="language.title + ' ' + $t('Component(Label):short-description')"
                                        :rules="language.code === 'en' ? 'required|min:3|max:255' : null"
                                    >
                                        <b-form-textarea
                                            :state="errors.length > 0 ? false:null"
                                            :id="'short-description' + language.code"
                                            v-model="news.translations[language.code].shortDescription"
                                            :placeholder="$t('Component(Placeholder):short-description')"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>

                                <b-form-group
                                    :label="$t('Component(Label):content')"
                                    :label-for="'content' + language.code"
                                >
                                    <quill-editor
                                        :id="'content' + language.code"
                                        v-model="news.translations[language.code].content"
                                        :options="snowOption"
                                    />
                                </b-form-group>
                            </b-tab>
                        </b-tabs>
                    </b-card>
                </b-col>
                <b-col md="4" lg="4">
                    <b-card title="Settings">

                        <b-form-group
                            :label="$t('Component(Label):slug')"
                            label-for="slug"
                        >
                            <validation-provider
                                #default="{ errors }"
                                :name="$t('Component(Label):slug')"
                                rules="required|min:3|max:245"
                            >
                                <b-form-input
                                    :state="errors.length > 0 ? false:null"
                                    id="slug"
                                    v-model="news.slug"
                                    :placeholder="$t('Component(Placeholder):slug')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>

                        <b-form-group
                            :label="$t('Component(Label):categories')"
                            label-for="categories"
                        >
                            <v-select
                                v-model="news.categories"
                                multiple
                                id="categories"
                                label="title"
                                :placeholder="$t('Component(Placeholder):categories')"
                                :options="categoryOptions"
                            />
                        </b-form-group>
                    </b-card>

                    <b-card title="Image">
                        <b-img
                            v-if="news.file"
                            ref="refPreviewEl"
                            fluid
                            :src="news.file ? news.file : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='"
                            class="rounded mr-2 mb-1 mb-md-0"
                        />
                        <b-img
                            v-else-if="news.image"
                            ref="refPreviewEl"
                            fluid
                            :src="news.image"
                            class="rounded mr-2 mb-1 mb-md-0"
                        />

                        <div>
                            <small class="text-muted">Max image size is 2mb.</small>
                            <b-card-text class="my-50">
                                <b-link id="blog-image-text">
                                    {{ news.file ? news.file.name : '-' }}
                                </b-link>
                            </b-card-text>
                            <div>
                                <b-form-file
                                    ref="refInputEl"
                                    v-model="news.file"
                                    accept=".jpg, .jpeg, .png, .gif"
                                    placeholder="Choose file"
                                    @input="inputImageRenderer"
                                />
                            </div>
                        </div>
                    </b-card>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <div class="text-right">
                        <b-button
                            v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                            variant="primary"
                            class="mr-1"
                            @click="save"
                        >
                            {{ $t('Component(Button):save') }}
                        </b-button>

                        <b-button
                            v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                            variant="primary"
                            @click="close"
                        >
                            {{ $t('Component(Button):close') }}
                        </b-button>
                    </div>
                </b-col>
            </b-row>
        </validation-observer>
    </b-overlay>
</template>

<script>
import {BButton, BTabs, BTab, BCard, BFormGroup, BFormInput, BRow, BCol, BFormCheckbox, BFormFile,
    BMediaAside, BMediaBody, BImg, BOverlay, BLink, BCardText, BFormTextarea } from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {i18nApi, PagesApi} from "@core/api";
import {required, between, max, min} from '@validations'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { quillEditor } from 'vue-quill-editor'
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils'
import { ref } from '@vue/composition-api'
import backendResponse from "@core/mixins/ui/backend-response";

export default {
    mixins: [backendResponse],
    directives: {Ripple},
    components: {
        BButton, BTabs, BTab, BCard, ValidationProvider, ValidationObserver, BFormGroup, BFormInput, BRow, BCol,
        quillEditor, BFormTextarea, BFormCheckbox, vSelect, BFormFile, BMediaAside, BMediaBody, BImg, BOverlay, BLink, BCardText
    },
    props: {
        id: {
            default: null
        }
    },
    data() {
        return {
            showLoading: true,
            languages: [],
            news: {
                slug: '',
                categories: [],
                visible: true,
                file: null,
                image: null
            },
            snowOption: {
                theme: 'snow',
            },
            categoryOptions: [],
            between,
            required,
            max,
            min,
        };
    },
    methods: {
        save() {
            this.$refs.news.validate().then(isValid => {
                if (!isValid) {
                    return;
                }
                if (this.id) {
                    PagesApi.updateNews(this.id, this.news).then(this.gotoTheGrid).catch(this.handleBackendError);
                } else {
                    PagesApi.createNews(this.news).then(this.gotoTheGrid).catch(this.handleBackendError);
                }
            });
        },
        close() {
            this.gotoTheGrid();
        },
        gotoTheGrid() {
            this.$router.push({name: 'administration-pages-news'});
        }
    },
    created() {
        const promises = [];
        if (this.id) {
            promises.push(
                PagesApi
                .getNews(this.id)
                .then(r => this.news = r.data)
            );
        }
        promises.push(i18nApi.getLanguages().then(r => {
            if (!this.news.translations) {
                this.news.translations = {};
            }
            r.data.forEach(language => {
                if (this.news.translations[language.code] === undefined) {
                    this.$set(this.news.translations, language.code, {title: '', shortDescription: '', content: ''});
                }
            });
            this.languages = r.data
        }));
        promises.push(PagesApi
            .categories(1, 100)
            .then(r => this.categoryOptions = r.data.items)
        );

        Promise.all(promises).then(() => this.showLoading = false);
    },
    setup() {
        const refInputEl = ref(null)
        const refPreviewEl = ref(null)

        const { inputImageRenderer } = useInputImageRenderer(refInputEl, base64 => { refPreviewEl.value.src = base64 })

        return {
            refInputEl,
            refPreviewEl,
            inputImageRenderer,
        }
    },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/quill.scss';
@import '@core/scss/vue/pages/page-blog.scss';
</style>
